import { TDashboardSegment } from '@api/lendingAPI/dashboardSegmentResponse.types';
import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { getIsSegmentationEligible } from '@helpers/GetIsSegmentationEligible/getIsSegmentationEligible';
import { TTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus.types';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { useQuery } from '@tanstack/react-query';

import { getDashboardSegmentQueryConfig } from './dashboardSegment.queryConfig';
import { hasSegmentLayout } from './segments/layouts';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { TAccountFeatureMap } from '../multiLoanDashboard/AccountFeatureEvaluator/AccountFeatureEvaluator';

const DASHBOARD_SEGMENTATION_SPLIT_TEST = 'uk_dashboard_segmentation_2026';

export const getIsSegmented = ({
    isEligible,
    branch,
    segment,
    headroom
}: {
    isEligible: boolean;
    branch: string | undefined;
    segment: TDashboardSegment | null;
    headroom: number | null;
}): boolean =>
    [
        isEligible,
        branch === 'INCLUDE',
        segment !== null && hasSegmentLayout(segment),
        segment !== 'offer_ready' || headroom !== null
    ].every(Boolean);

export const useDashboardSegment = ({
    accountId,
    features,
    loanStatus,
    topupStatus,
    productType
}: {
    accountId: string;
    features: TAccountFeatureMap | undefined;
    loanStatus: TAccount['loan_status'];
    topupStatus: TTopUpStatus;
    productType: TAccount['product_type'] | undefined;
}) => {
    const stateKey = useStateKey();

    const isEligible = getIsSegmentationEligible({
        country: getCountry(),
        productType,
        loanStatus,
        topupStatus,
        isRevolvingCreditFacility: Boolean(features?.revolving_credit_facility)
    });

    // TODO(TRT-2384): drop cookieOnly once the layout designs are implemented,
    // which is what activates the split test.
    const { branch } = useSplitTestBranch({
        test: DASHBOARD_SEGMENTATION_SPLIT_TEST,
        enabled: isEligible,
        cookieOnly: true
    });

    const { data } = useQuery({
        ...getDashboardSegmentQueryConfig(stateKey, accountId),
        enabled: isEligible && Boolean(accountId),
        suspense: isEligible && branch === 'INCLUDE'
    });

    const segment = data?.segment ?? null;

    return {
        isEligible,
        branch,
        segment,
        isSegmented: getIsSegmented({
            isEligible,
            branch,
            segment,
            headroom: data?.headroom ?? null
        }),
        isSegmentResolved: !isEligible || data !== undefined
    };
};
